import { onError } from '@apollo/client/link/error';
import type { Brand } from '@seek/audience-zones';
import {
  createClient,
  createMockClient,
  type ClientOptions,
} from '@seek/ca-graphql-client';
import type { browserHttpLink } from '@seek/ca-graphql-client/browser';
import type { nodeHttpLink } from '@seek/ca-graphql-client/node';
import {
  createUrlResolver,
  getSiteFromZoneAndProduct,
  type Country,
  type Language,
  type MelwaysZone,
} from '@seek/melways-sites';
import { metrics } from '@seek/metrics-js';

import mockOverrides from 'src/mock-overrides';
import resolverOverrides from 'src/resolver-overrides';

import { appName } from './config';
import logger from './modules/logger';

export interface Client {
  environment: string;
  httpLink: typeof browserHttpLink | typeof nodeHttpLink;
  brand: Brand;
  country: Country;
  graphqlEndpoint?: string;
}

export const redirectToSignIn = () => {
  if (window) {
    const site = getSiteFromZoneAndProduct(
      window.APP_CONFIG.zone as MelwaysZone,
      'candidate',
    );
    const language = window.APP_CONFIG.language as Language;
    const resolveUrl = createUrlResolver({
      site,
      language,
    });
    const { pathname, search, hash } = window.location;
    const returnUrl = `${pathname}${search}${hash}`;
    window.location.href = resolveUrl({
      path: `/oauth/login/?returnUrl=${returnUrl}`,
    });
  }
};

const linkError = onError(({ graphQLErrors, networkError }) => {
  metrics.count('graphqlError');
  if (graphQLErrors) {
    graphQLErrors.forEach(({ message, locations, path, extensions }) => {
      const errorText = `[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`;
      logger(errorText);
      if (extensions?.code === 'UNAUTHENTICATED') {
        redirectToSignIn();
      }
    });
  }

  if (networkError) {
    if ('statusCode' in networkError && networkError.statusCode === 401) {
      redirectToSignIn();
    }
    logger(`[Network error]: ${networkError}`);
  }
});

const getClient = ({ httpLink, brand, country, graphqlEndpoint }: Client) => {
  const links = [linkError];

  const shouldUseMocks = process.env.NODE_ENV === 'development';
  return shouldUseMocks
    ? createMockClient({
        ...(graphqlEndpoint
          ? { graphqlEndpointOverride: graphqlEndpoint }
          : {}),
        mocks: mockOverrides(),
        resolvers: resolverOverrides(),
      })
    : createClient({
        links,
        appName,
        authOptions: {
          useAuth: true,
          forceStrategy: 'AUTH0',
        },
        httpLink,
        brand: brand as ClientOptions['brand'],
        country: country as ClientOptions['country'],
      });
};

export default getClient;
